import type { StorageUsage } from '~/gql/storage/storage'
import type { ModuleRow } from '~/pages/backoffice/admin/modules/ModulesList'
import { useMutation, useQuery } from '@apollo/client/react'
import { TrainerCapability } from '@zeus/index'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { GQL_DELETE_SCORM_MODULE, GQL_SCORM_MODULES } from '~/gql/scorm-modules/scorm-modules'
import { GQL_STORAGE_USAGE } from '~/gql/storage/storage'
import { tourAnchor } from '~/helpers/tour/anchors'
import { useCan } from '~/hooks/use-can'
import { moduleDeleteError } from '~/pages/backoffice/admin/modules/module-delete-error'
import ModuleDeleteDialog from '~/pages/backoffice/admin/modules/ModuleDeleteDialog'
import ModuleImportCard from '~/pages/backoffice/admin/modules/ModuleImportCard'
import ModuleRenameDialog from '~/pages/backoffice/admin/modules/ModuleRenameDialog'
import ModuleReplaceDialog from '~/pages/backoffice/admin/modules/ModuleReplaceDialog'
import ModulesList from '~/pages/backoffice/admin/modules/ModulesList'
import StorageUsageCard from '~/pages/backoffice/admin/modules/StorageUsageCard'

/**
 * Les modules de l'organisation : quota, import, liste. L'import, le
 * remplacement du contenu (#311), le renommage et la suppression n'apparaissent
 * qu'à qui en a le droit (#11) — remplacer et renommer demandent le droit
 * d'import.
 */
function ModulesTable() {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_SCORM_MODULES, {
    fetchPolicy: 'cache-and-network',
  })
  const { data: storageData, refetch: refetchStorage } = useQuery(GQL_STORAGE_USAGE, {
    fetchPolicy: 'cache-and-network',
  })
  const [deleteModule] = useMutation(GQL_DELETE_SCORM_MODULE)
  const canImport = useCan(TrainerCapability.MODULES_IMPORT)
  const canDelete = useCan(TrainerCapability.MODULES_DELETE)
  const [removing, setRemoving] = useState<ModuleRow | null>(null)
  const [replacing, setReplacing] = useState<ModuleRow | null>(null)
  const [renaming, setRenaming] = useState<ModuleRow | null>(null)

  const modules = data?.scormModules ?? []
  const storageUsage = storageData?.storageUsage as unknown as StorageUsage | undefined
  // Un import, un remplacement comme une suppression change le disque : le compteur doit suivre tout de suite.
  const refresh = () => Promise.all([refetch(), refetchStorage()])

  const handleDelete = async (id: string) => {
    try {
      await deleteModule({ variables: { id } })
      toast.success(t('modules.deleted'))
      await refresh()
    } catch (error) {
      const cause = moduleDeleteError(error)
      toast.error(cause.kind === 'inUse'
        ? cause.sessions.length > 0
          ? t('modules.inUse', { sessions: cause.sessions.join(', ') })
          : t('modules.inUseUnnamed')
        : t('modules.deleteFailed'))
    }
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Avant le formulaire d'import : ce qu'il reste se lit AVANT de choisir
          un fichier, pas après le refus. */}
      <div {...tourAnchor('modules.storage')}>
        <StorageUsageCard usage={storageUsage} />
      </div>

      {canImport && <ModuleImportCard onImported={refresh} maxUploadBytes={storageUsage?.maxUploadBytes} />}

      <ModulesList
        modules={modules}
        loading={loading}
        onDelete={canDelete ? setRemoving : undefined}
        onReplace={canImport ? setReplacing : undefined}
        onRename={canImport ? setRenaming : undefined}
      />

      {renaming && <ModuleRenameDialog module={renaming} onClose={() => setRenaming(null)} />}

      {replacing && (
        <ModuleReplaceDialog module={replacing} maxUploadBytes={storageUsage?.maxUploadBytes} onClose={() => setReplacing(null)} onReplaced={refresh} />
      )}

      {removing && (
        <ModuleDeleteDialog
          module={removing}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            void handleDelete(removing.id)
            setRemoving(null)
          }}
        />
      )}
    </div>
  )
}

export default ModulesTable
